import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert, Button, Checkbox, DatePicker, Modal, Plus, Select, TextInput, Trash01 } from '@opendate-app/setlist';

import useAutoSave from '../../shared/components/useAutoSave';
import ContactQuickCreateForm from '../../Deals/components/ContactQuickCreateForm';
import SearchPicker from './SearchPicker';

const VALIDATION_COPY = 'Grant name, organization and funder are required.';
const ROW_VALIDATION_COPY = 'Each reporting deadline needs a label and a date.';
const CREATE_ORGANIZATION_COPY = "Couldn't create the organization. Try again.";
const MAX_FUNDER_SUGGESTIONS = 8;

const contactCreateLabel = (term) => `Create new contact "${term}"`;
const fanLabel = (fan) => fan.name || [fan.first_name, fan.last_name].filter(Boolean).join(' ');

const toDate = (ymd) => (ymd ? new Date(`${ymd.substring(0, 10)}T00:00:00`) : null);

const toYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const toAmountString = (raw) => {
  const text = String(raw || '').trim();
  const cleaned = text.replace(/[^0-9.]/g, '');
  if (cleaned === '') return null;
  return text.startsWith('-') ? `-${cleaned}` : cleaned;
};

const amountField = (raw) => {
  if (raw === null || raw === undefined) return '';
  return String(raw);
};

let rowSequence = 0;
const nextRowKey = () => {
  rowSequence += 1;
  return `row-${rowSequence}`;
};

const blankRow = () => ({ key: nextRowKey(), id: null, label: '', due_on: null, completed_at: null, removed: false });

const rowsFromGrant = (grant, addReportingDeadline) => {
  if (!grant) return [blankRow()];
  const rows = (grant.reporting_deadlines || []).map((d) => ({
    key: nextRowKey(),
    id: d.id,
    label: d.label || '',
    due_on: d.due_on ? d.due_on.substring(0, 10) : null,
    completed_at: d.completed_at || null,
    removed: false,
  }));
  return addReportingDeadline ? [...rows, blankRow()] : rows;
};

const AWARDED_STAGES = ['awarded', 'complete'];

const reminderHint = { margin: 'var(--setlist-spacing-xs) 0 0', color: 'var(--sl-text-tertiary)', fontSize: 'var(--setlist-text-sm-size)' };

const ownerFor = (grant, currentOwnerId) => {
  const owner = grant ? grant.owner_team_membership_id : currentOwnerId;
  return owner ? String(owner) : null;
};

const GrantFormBody = ({ isOpen, onClose, grant, team, csrfToken, owners, currentOwnerId, stages, funders, onSaved, addReportingDeadline }) => {
  const [name, setName] = useState(grant ? grant.name || '' : '');
  const [organization, setOrganization] = useState(grant && grant.organization_id ? { value: grant.organization_id, label: grant.organization_name || 'Archived organization' } : null);
  const [contact, setContact] = useState(grant && grant.primary_fan_id ? { value: grant.primary_fan_id, label: grant.primary_fan_name || 'Unnamed contact' } : null);
  const [funder, setFunder] = useState(grant ? grant.funder || '' : '');
  const [ownerId, setOwnerId] = useState(ownerFor(grant, currentOwnerId));
  const [stage, setStage] = useState(grant ? grant.stage : 'prospect');
  const [requestedAmount, setRequestedAmount] = useState(grant ? amountField(grant.requested_amount) : '');
  const [awardedAmount, setAwardedAmount] = useState(grant ? amountField(grant.awarded_amount) : '');
  const [description, setDescription] = useState(grant ? grant.description || '' : '');
  const [applicationDeadlineOn, setApplicationDeadlineOn] = useState(grant ? grant.application_deadline_on : null);
  const [submittedOn, setSubmittedOn] = useState(grant ? grant.submitted_on : null);
  const [decisionOn, setDecisionOn] = useState(grant ? grant.decision_on : null);
  const [rows, setRows] = useState(() => rowsFromGrant(grant, addReportingDeadline));
  const [remindersEnabled, setRemindersEnabled] = useState(grant ? grant.reminders_enabled !== false : true);
  const [validationError, setValidationError] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [contactDraft, setContactDraft] = useState(null);
  const closingRef = useRef(false);
  const formRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeUnlessPopupOpen = (event) => {
      if (event.key !== 'Escape') return;
      if (formRef.current && formRef.current.querySelector('[aria-expanded="true"]')) return;
      event.stopPropagation();
      onCloseRef.current();
    };
    window.addEventListener('keydown', closeUnlessPopupOpen, true);
    return () => window.removeEventListener('keydown', closeUnlessPopupOpen, true);
  }, [isOpen]);

  const handleSaved = (saved, isNew) => {
    if (closingRef.current) return;
    closingRef.current = true;
    onSaved(saved, isNew);
    onClose();
  };

  const { saveFields, saveStatus, errors } = useAutoSave({
    team,
    csrfToken,
    resourcePath: 'crm/grants',
    resourceKey: 'grant',
    existingId: grant ? grant.id : null,
    onSaved: handleSaved,
  });

  const searchOrganizations = (term) => axios
    .get(`/teams/${team.id}/crm/organizations/search`, { params: { q: term } })
    .then((res) => res.data.map((o) => ({ value: o.id, label: o.name })));

  const createOrganization = (term) => {
    setCreateError(null);
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    return axios
      .post(`/teams/${team.id}/crm/organizations.json`, { organization: { name: term.trim() } })
      .then((res) => setOrganization({ value: res.data.id, label: res.data.name }))
      .catch((e) => {
        const messages = e && e.response && e.response.data;
        setCreateError(Array.isArray(messages) && messages.length > 0 ? messages.join(', ') : CREATE_ORGANIZATION_COPY);
      });
  };

  const changeOrganization = (next) => {
    setCreateError(null);
    setOrganization(next);
  };

  const changeContact = (next) => {
    setContact(next);
    if (next) setContactDraft(null);
  };

  const handleContactCreated = (fan) => {
    setContact({ value: fan.id, label: fanLabel(fan) });
    setContactDraft(null);
  };

  const searchContacts = (term) => axios
    .get(`/teams/${team.id}/crm/fans/autocomplete`, { params: { name: term } })
    .then((res) => res.data.map((f) => ({ value: f.id, label: f.name, description: f.title_email_preview })));

  const searchFunders = (term) => {
    const needle = term.toLowerCase();
    const matches = (funders || []).filter((f) => f.toLowerCase().includes(needle)).slice(0, MAX_FUNDER_SUGGESTIONS);
    return Promise.resolve(matches.map((f) => ({ value: f, label: f })));
  };

  const updateRow = (key, attrs) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...attrs } : r)));

  const removeRow = (row) => {
    if (row.id) {
      updateRow(row.key, { removed: true });
    } else {
      setRows((prev) => prev.filter((r) => r.key !== row.key));
    }
  };

  const reportingPayload = () => rows.reduce((acc, row) => {
    if (row.removed) {
      acc.push({ id: row.id, _destroy: true });
    } else if (row.id && row.label.trim() === '' && !row.due_on) {
      acc.push({ id: row.id, _destroy: true });
    } else if (row.label.trim() !== '' || row.due_on) {
      if (row.id) {
        acc.push({ id: row.id, label: row.label, due_on: row.due_on, completed_at: row.completed_at });
      } else {
        acc.push({ label: row.label, due_on: row.due_on });
      }
    }
    return acc;
  }, []);

  const handleSave = () => {
    if (name.trim() === '' || !organization || funder.trim() === '') {
      setValidationError(VALIDATION_COPY);
      return;
    }
    const partialRow = rows.some((r) => !r.removed && (r.label.trim() === '') !== !r.due_on);
    if (partialRow) {
      setValidationError(ROW_VALIDATION_COPY);
      return;
    }
    setValidationError(null);
    saveFields({
      name,
      funder,
      description,
      stage,
      requested_amount: toAmountString(requestedAmount),
      awarded_amount: toAmountString(awardedAmount),
      application_deadline_on: applicationDeadlineOn,
      submitted_on: submittedOn,
      decision_on: decisionOn,
      organization_id: organization.value,
      primary_fan_id: contact ? contact.value : null,
      owner_team_membership_id: ownerId,
      reminders_enabled: remindersEnabled,
      grant_reporting_deadlines_attributes: reportingPayload(),
    });
  };

  const isEditing = Boolean(grant);
  const saving = saveStatus === 'saving';
  const awardedEnabled = AWARDED_STAGES.includes(stage);
  const visibleRows = rows.filter((r) => !r.removed);

  const dateField = (label, value, setValue) => (
    <DatePicker
      label={label}
      placeholder="Select date"
      value={toDate(value)}
      onChange={(date) => setValue(toYmd(date))}
    />
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--setlist-spacing-md)' }}>
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {isEditing ? 'Save grant' : 'Create grant'}
      </Button>
    </div>
  );

  const section = (title) => (
    <div style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{title}</div>
  );

  const grid = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--setlist-spacing-lg)' };
  const threeColumns = { ...grid, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' };
  const stack = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-lg)' };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeOnEsc={false}
      width={720}
      title={isEditing ? 'Edit grant' : 'New grant'}
      description="Link the grant to an organization in your CRM so it shows up on their record."
      footer={footer}
      className="grants-form-modal"
    >
      <div ref={formRef} style={stack}>
        {validationError && <Alert color="error" title={validationError} />}
        {createError && <Alert color="error" title={createError} />}
        {errors.length > 0 && <Alert color="error" title="Please fix the following" description={errors.join(', ')} />}

        {section('Grant details')}
        <TextInput
          label="Grant name"
          required
          placeholder="e.g. General Operating Support FY27"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="grants-form-grid" style={grid}>
          <SearchPicker
            label="Organization"
            required
            placeholder="Search organizations"
            value={organization}
            onChange={changeOrganization}
            search={searchOrganizations}
            onCreate={createOrganization}
          />
          <div>
            <SearchPicker
              label="Primary contact"
              placeholder="Search contacts"
              value={contact}
              onChange={changeContact}
              search={searchContacts}
              onCreate={setContactDraft}
              createLabel={contactCreateLabel}
            />
            {contactDraft !== null && (
              <ContactQuickCreateForm
                key={contactDraft}
                team={team}
                csrfToken={csrfToken}
                initialQuery={contactDraft}
                onCreated={handleContactCreated}
                onCancel={() => setContactDraft(null)}
              />
            )}
          </div>
        </div>
        <div className="grants-form-grid" style={grid}>
          <SearchPicker
            label="Funder"
            required
            freeText
            placeholder="Foundation, agency or program"
            value={funder}
            onChange={setFunder}
            search={searchFunders}
          />
          <Select
            label="Owner"
            placeholder="Unassigned"
            clearable
            options={owners.map((o) => ({ value: String(o.value), label: o.label }))}
            value={ownerId}
            onChange={(value) => setOwnerId(value || null)}
          />
        </div>
        <div className="grants-form-grid" style={threeColumns}>
          <Select
            label="Status"
            options={stages}
            value={stage}
            onChange={(value) => setStage(value || 'prospect')}
          />
          <TextInput
            label="Requested amount"
            leadingText="$"
            placeholder="0"
            value={requestedAmount}
            onChange={(e) => setRequestedAmount(e.target.value)}
          />
          <TextInput
            label="Awarded amount"
            leadingText="$"
            placeholder={awardedEnabled ? '0' : 'Set status to Awarded'}
            disabled={!awardedEnabled}
            value={awardedAmount}
            onChange={(e) => setAwardedAmount(e.target.value)}
          />
        </div>
        <TextInput
          label="Description"
          multiline
          rows={3}
          placeholder="What the funding supports, eligibility notes, program officer…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {section('Key dates')}
        <div className="grants-form-grid" style={threeColumns}>
          {dateField('Application deadline', applicationDeadlineOn, setApplicationDeadlineOn)}
          {dateField('Submission date', submittedOn, setSubmittedOn)}
          {dateField('Award decision date', decisionOn, setDecisionOn)}
        </div>

        {section('Reporting deadlines')}
        {visibleRows.map((row) => (
          <div key={row.key} style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--setlist-spacing-md)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextInput
                label="Label"
                placeholder="e.g. Interim report"
                value={row.label}
                onChange={(e) => updateRow(row.key, { label: e.target.value })}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <DatePicker
                label="Due date"
                placeholder="Select date"
                value={toDate(row.due_on)}
                onChange={(date) => updateRow(row.key, { due_on: toYmd(date) })}
              />
            </div>
            <Button
              variant="tertiary"
              icon={Trash01}
              aria-label="Remove reporting deadline"
              onClick={() => removeRow(row)}
            />
          </div>
        ))}
        <div>
          <Button variant="secondary" size="sm" icon={Plus} onClick={() => setRows((prev) => [...prev, blankRow()])}>
            Add reporting deadline
          </Button>
        </div>
        <div>
          <Checkbox
            label="Send deadline reminders"
            checked={remindersEnabled}
            onChange={(event) => setRemindersEnabled(event.target.checked)}
          />
          <p style={reminderHint}>
            {isEditing
              ? "Email reminders before each open deadline. Adjust timing and recipients on the grant's Key dates tab."
              : "Email the grant owner 14, 7 and 1 day before each open deadline. Adjust on the grant's Key dates tab."}
          </p>
        </div>
        <div className="grants-form-scroll-fade" aria-hidden="true" />
      </div>
    </Modal>
  );
};

const GrantFormModal = (props) => {
  const { isOpen, grant } = props;
  const sessionRef = useRef({ open: false, count: 0 });
  if (isOpen && !sessionRef.current.open) sessionRef.current.count += 1;
  sessionRef.current.open = isOpen;
  const key = `${sessionRef.current.count}-${grant ? grant.id : 'new'}`;
  return <GrantFormBody key={key} {...props} />;
};

GrantFormModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  grant: PropTypes.object,
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string,
  owners: PropTypes.array,
  currentOwnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stages: PropTypes.array,
  funders: PropTypes.array,
  onSaved: PropTypes.func.isRequired,
  addReportingDeadline: PropTypes.bool,
};

GrantFormModal.defaultProps = {
  isOpen: false,
  grant: null,
  csrfToken: '',
  owners: [],
  currentOwnerId: null,
  stages: [],
  funders: [],
  addReportingDeadline: false,
};

export default GrantFormModal;
